import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { EngagementEntry } from "@shared/types/engagement";

/**
 * Presentation shared by the premise engagement panel and the chain report, so
 * a duration or a ranked list reads identically wherever an operator meets it.
 */

/**
 * Seconds → the shortest phrase that is still honest at that magnitude.
 *
 * Rounding matters here: "0h" for a 40-minute total makes a real number look
 * like an absent one, and a report a customer is paying for cannot round its
 * own evidence away. So the unit steps down as the value shrinks rather than
 * fixing on hours, and a genuine zero renders as an em dash so it is legible as
 * "nothing happened" rather than "something happened, we measured 0".
 */
export function formatDuration(seconds: number | undefined | null): string {
  if (!seconds || seconds <= 0) return "-";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 10) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours).toLocaleString()}h`;
}

/** A 0-1 ratio as a percentage, or an em dash when there is nothing to divide. */
export function formatPercent(ratio: number | undefined | null): string {
  if (ratio === undefined || ratio === null || !Number.isFinite(ratio)) {
    return "-";
  }
  return `${Math.round(ratio * 100)}%`;
}

/** One headline number, with an optional line of context beneath it. */
export function EngagementMetric({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <Box>
      <Typography variant="h4">
        {typeof value === "number" ? value.toLocaleString() : value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.disabled" display="block">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/** How many rows a breakdown shows before it stops. */
const TOP_N = 8;

/**
 * A ranked breakdown with a share bar. The bar is scaled to the top row rather
 * than to the total, so a long tail stays readable instead of collapsing into
 * a row of invisible slivers.
 */
export function EngagementBreakdown({
  title,
  entries,
  empty,
}: {
  title: string;
  entries: EngagementEntry[];
  empty: string;
}) {
  const top = entries.slice(0, TOP_N);
  const max = top[0]?.count ?? 0;
  return (
    <Box sx={{ flex: "1 1 260px", minWidth: 240 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {top.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {top.map((e) => (
            <Box key={e.id}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" noWrap title={e.label}>
                  {e.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {e.count.toLocaleString()}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={max ? (e.count / max) * 100 : 0}
                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
              />
            </Box>
          ))}
          {entries.length > top.length && (
            <Typography variant="caption" color="text.secondary">
              +{entries.length - top.length} more
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
